/*
  EGODRIFT custom Kv line-item property controller
  Version: 1.0.6

  Shopify production filename:
  assets/ego-custom-kv.js

  Changes in this version:
  - Keeps the Custom Kv field outside the Dawn variant-selects element to avoid
    variant-picker side effects.
  - Uses event-driven state instead of repeatedly re-deciding visibility from
    transient variant states.
  - Only hides the field after a direct Motor Kv change to a non-custom value.
  - Keeps the target Kv input optional.
  - Mirrors the optional target Kv value into a hidden input inside the real
    product form as properties[Custom Kv].
*/
(() => {
  if (window.__egoCustomKvInitialized) return;
  window.__egoCustomKvInitialized = true;

  const WRAPPER_SELECTOR = '[data-ego-custom-kv]';
  const INPUT_SELECTOR = '[data-ego-custom-kv-input]';
  const PICKER_SELECTOR = 'variant-selects, variant-radios';
  const PROPERTY_NAME = 'properties[Custom Kv]';
  const MIRROR_SELECTOR = '[data-ego-custom-kv-property]';

  const customKv = document.querySelector(WRAPPER_SELECTOR);
  if (!customKv) return;

  const input = customKv.querySelector(INPUT_SELECTOR);
  let active = false;
  let updateTimer = null;

  function normalize(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function isCustomKvValue(value) {
    const normalized = normalize(value);
    return normalized === 'custom kv' || (normalized.includes('custom') && normalized.includes('kv'));
  }

  function isMotorKvLabel(value) {
    const normalized = normalize(value);
    return normalized === 'motor kv' || normalized.includes('motor kv');
  }

  function cleanText(element) {
    if (!element) return '';
    const clone = element.cloneNode(true);
    clone.querySelectorAll('[data-selected-value], .visually-hidden, .label-unavailable').forEach((node) => node.remove());
    return clone.textContent || '';
  }

  function getProductScope() {
    return (
      customKv.closest('product-info') ||
      customKv.closest('.product__info-container') ||
      customKv.closest('.product') ||
      customKv.closest('section') ||
      document
    );
  }

  function getPickers() {
    const scope = getProductScope();
    const scopedPickers = Array.from(scope.querySelectorAll(PICKER_SELECTOR));
    return scopedPickers.length ? scopedPickers : Array.from(document.querySelectorAll(PICKER_SELECTOR));
  }

  function getLabelForInput(inputElement) {
    if (!inputElement || !inputElement.id) return null;
    return document.querySelector(`label[for="${CSS.escape(inputElement.id)}"]`);
  }

  function getInputFromLabel(label) {
    if (!label) return null;

    const forId = label.getAttribute('for');
    if (forId) return document.getElementById(forId);

    return label.querySelector('input');
  }

  function inputLooksCustom(inputElement) {
    if (!inputElement) return false;
    const label = getLabelForInput(inputElement);
    return isCustomKvValue(inputElement.value) || isCustomKvValue(cleanText(label));
  }

  function findMotorKvFieldset() {
    for (const picker of getPickers()) {
      const fieldsets = Array.from(picker.querySelectorAll('fieldset'));
      const fieldset = fieldsets.find((item) => {
        const legend = item.querySelector('legend');
        return legend && isMotorKvLabel(cleanText(legend));
      });

      if (fieldset) return fieldset;
    }

    return null;
  }

  function findMotorKvDropdown() {
    for (const picker of getPickers()) {
      const dropdowns = Array.from(picker.querySelectorAll('.product-form__input--dropdown'));
      const dropdown = dropdowns.find((item) => {
        const label = item.querySelector('label.form__label, .form__label');
        return label && isMotorKvLabel(cleanText(label));
      });

      if (dropdown) return dropdown;
    }

    return null;
  }

  function getMotorKvState() {
    const fieldset = findMotorKvFieldset();
    if (fieldset) {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (!checked) return 'unknown';
      return inputLooksCustom(checked) ? 'custom' : 'non-custom';
    }

    const dropdown = findMotorKvDropdown();
    if (dropdown) {
      const select = dropdown.querySelector('select');
      if (!select) return 'unknown';
      const selectedOption = select.options[select.selectedIndex];
      const selectedText = selectedOption ? selectedOption.textContent : '';
      return isCustomKvValue(select.value) || isCustomKvValue(selectedText) ? 'custom' : 'non-custom';
    }

    return 'unknown';
  }

  function getBroadSelectedCustomState() {
    for (const picker of getPickers()) {
      const checkedInputs = Array.from(picker.querySelectorAll('input[type="radio"]:checked'));
      if (checkedInputs.some((radio) => inputLooksCustom(radio))) return 'custom';

      const selects = Array.from(picker.querySelectorAll('select'));
      const hasCustomSelect = selects.some((select) => {
        const selectedOption = select.options[select.selectedIndex];
        return isCustomKvValue(select.value) || isCustomKvValue(selectedOption ? selectedOption.textContent : '');
      });

      if (hasCustomSelect) return 'custom';
    }

    return 'unknown';
  }

  function isCustomKvInUrl() {
    const params = new URLSearchParams(window.location.search);
    const configuration = params.get('configuration');
    return isCustomKvValue(configuration) || normalize(configuration).includes('customkv');
  }

  function placeFieldOutsideVariantPicker() {
    const picker = getPickers()[0];
    if (!picker) return;

    // Keep the wrapper directly after the picker instead of inside it. Putting a
    // text input inside Dawn's variant-selects can make Dawn treat target-Kv
    // input changes as variant-picker changes.
    if (customKv.previousElementSibling !== picker) {
      picker.insertAdjacentElement('afterend', customKv);
    }
  }

  function findProductForm() {
    const explicitFormId = customKv.dataset.productFormId || (input && input.getAttribute('form'));
    if (explicitFormId) {
      const explicitForm = document.getElementById(explicitFormId);
      if (explicitForm) return explicitForm;
    }

    const scope = getProductScope();
    return scope.querySelector('product-form form') || document.querySelector('product-form form');
  }

  function getOrCreateMirror(form) {
    if (!form) return null;

    let mirror = form.querySelector(MIRROR_SELECTOR);
    if (!mirror) {
      mirror = document.createElement('input');
      mirror.type = 'hidden';
      mirror.setAttribute('data-ego-custom-kv-property', '');
      form.appendChild(mirror);
    }

    mirror.name = PROPERTY_NAME;
    return mirror;
  }

  function syncLineProperty() {
    const form = findProductForm();
    const mirror = getOrCreateMirror(form);
    const value = input ? (input.value || '').trim() : '';

    if (input) {
      // UI-only visible input. The hidden mirror inside the actual product form
      // is the submitted Shopify line-item property.
      input.removeAttribute('name');
      input.removeAttribute('form');
      input.disabled = !active;
      input.required = false;
      input.autocomplete = 'off';
      input.setCustomValidity('');
    }

    if (mirror) {
      mirror.disabled = !active || !value;
      mirror.value = active && value ? value : '';
    }
  }

  function render() {
    placeFieldOutsideVariantPicker();

    customKv.hidden = false;
    customKv.classList.toggle('ego-custom-kv--active', active);
    customKv.setAttribute('aria-hidden', active ? 'false' : 'true');

    syncLineProperty();
  }

  function setActive(nextActive) {
    active = Boolean(nextActive);
    render();
  }

  function initializeState() {
    const motorState = getMotorKvState();

    if (motorState === 'custom') {
      active = true;
    } else if (motorState === 'non-custom') {
      active = false;
    } else if (getBroadSelectedCustomState() === 'custom' || isCustomKvInUrl()) {
      active = true;
    }

    render();
  }

  function scheduleRender(delay = 0) {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(render, delay);
  }

  function handleMotorKvChange(target) {
    const motorFieldset = findMotorKvFieldset();
    const motorDropdown = findMotorKvDropdown();

    if (motorFieldset && motorFieldset.contains(target)) {
      const radio = target.matches('input[type="radio"]') ? target : motorFieldset.querySelector('input[type="radio"]:checked');
      setActive(inputLooksCustom(radio));
      return true;
    }

    if (motorDropdown && motorDropdown.contains(target)) {
      const select = target.matches('select') ? target : motorDropdown.querySelector('select');
      const selectedOption = select ? select.options[select.selectedIndex] : null;
      setActive(Boolean(select) && (isCustomKvValue(select.value) || isCustomKvValue(selectedOption ? selectedOption.textContent : '')));
      return true;
    }

    return false;
  }

  window.EgoCustomKv = {
    sync: render,
    isActive() {
      return active;
    },
    getValue() {
      syncLineProperty();
      return input && active ? (input.value || '').trim() : '';
    },
    getPropertyName() {
      return PROPERTY_NAME;
    },
  };

  if (input) {
    input.addEventListener('input', () => scheduleRender(0));
    input.addEventListener('change', () => scheduleRender(0));
  }

  document.addEventListener('click', (event) => {
    const motorFieldset = findMotorKvFieldset();
    if (!motorFieldset) return;

    const targetLabel = event.target.closest('label');
    const targetInput = event.target.closest('input[type="radio"]');
    const candidate = targetInput || getInputFromLabel(targetLabel);

    if (candidate && motorFieldset.contains(candidate)) {
      setActive(inputLooksCustom(candidate));
      window.setTimeout(() => setActive(inputLooksCustom(candidate)), 0);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target === input) {
      scheduleRender(0);
      return;
    }

    if (handleMotorKvChange(event.target)) return;

    // Non-Motor-Kv variant changes must not hide the field. They may replace or
    // refresh other variant state, so only re-sync placement/property mirrors.
    if (event.target.closest(PICKER_SELECTOR)) {
      scheduleRender(0);
      scheduleRender(120);
    }
  });

  document.addEventListener('ego:variant-change', () => {
    // Do not re-decide false here; this event can fire during transient variant
    // refreshes. Keep the current active state and only repair placement/mirror.
    scheduleRender(0);
    scheduleRender(120);
  });

  document.addEventListener('shopify:section:load', () => window.setTimeout(initializeState, 120));
  window.addEventListener('popstate', () => window.setTimeout(initializeState, 80));
  window.addEventListener('pageshow', () => window.setTimeout(initializeState, 80));

  initializeState();
  window.setTimeout(initializeState, 150);
})();
